import { add, length, normalize, scale, sub } from './math.ts';
import type { MovementArea, Vec2 } from './types.ts';

/**
 * Landing a projected point *exactly* on the rim is a coin flip in floating point: `normalize`
 * involves a square root, so the result can measure a few ulps outside `radius`, and
 * `isValidDestination` rejects anything past it. That rejection clears the hover, so an
 * exactly-on-rim projection makes the aim dot and prediction circle flicker as the pointer moves.
 * Seating the point a hair inside keeps every projection legal. The inset is ~1e-7 metres.
 */
const RIM_INSET = 1e-6;

/**
 * Maps a raw pointer world position to an effective aim point for the planning circle.
 *
 * - Inside the circle: returned unchanged regardless of the toggle.
 * - In the outer halo (`dist - radius <= overflowWorld`): projected onto the rim so the player
 *   can easily lock to the circle's edge without overshooting.
 * - Otherwise: `null` — the pointer is outside the catchable zone.
 *
 * The stick is input-feel only. The simulation still validates whatever point is ultimately passed
 * to `isValidDestination` or `tryCommit`.
 */
export function pointInMovementDisk(point: Vec2, area: MovementArea): boolean {
  return length(sub(point, area.center)) <= area.radius;
}

/** True when this screen position has not yet been used to derive dest. */
export function pointerScreenMoved(
  last: Vec2 | null,
  screen: Vec2,
): boolean {
  return last === null || last.x !== screen.x || last.y !== screen.y;
}

/**
 * Dest follows the pointer only when the pointer itself moved. Camera pan/zoom
 * must not slide a parked dest. A pointer that left the catch zone clears dest.
 */
export function hoverAfterCameraMove(
  current: Vec2 | null,
  next: Vec2 | null,
  pointerMoved: boolean,
): Vec2 | null {
  return pointerMoved ? next : current;
}

export function stickToMovementRim(
  point: Vec2,
  area: MovementArea,
  opts: {
    /** When false the halo projection is skipped; inside points still pass through. */
    enabled: boolean;
    /** World-pixel depth of the catch zone just outside the rim. */
    overflowWorld: number;
  },
): Vec2 | null {
  const offset = sub(point, area.center);
  const dist = length(offset);

  if (dist <= area.radius) {
    return point;
  }

  if (opts.enabled && dist - area.radius <= opts.overflowWorld) {
    const seated = Math.max(0, area.radius - RIM_INSET);
    return add(area.center, scale(normalize(offset), seated));
  }

  return null;
}
